import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL_SERVER } from '../../api/api';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import { startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';

const PnLCalendar = ({ accountId, strategy, combined = false, userRole, showTrades }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [pnlData, setPnlData] = useState({});
    const [balance, setBalance] = useState(null);
    const [showBalance, setShowBalance] = useState(false);
    const [error, setError] = useState('');
    const [selectedDay, setSelectedDay] = useState(null);
    const [trades, setTrades] = useState([]);
    const [showTradesModal, setShowTradesModal] = useState(false);

    const [showCapitalModal, setShowCapitalModal] = useState(false);
    const [baseCapital, setBaseCapital] = useState(0); 
    const [tempCapitalInput, setTempCapitalInput] = useState('');

    // Monthly trade counts (from API)
    const [monthlyProfitableTrades, setMonthlyProfitableTrades] = useState(0);
    const [monthlyLosingTrades, setMonthlyLosingTrades] = useState(0);
    const [monthlyTotalLots, setMonthlyTotalLots] = useState(0);

    const timeZone = 'Asia/Kolkata';
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
    ];

    useEffect(() => {
        const fetchPnL = async () => {
            setError('');
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setError('Please log in to view PnL data');
                    return;
                }

                let response;
                if (combined && Array.isArray(accountId)) {
                    response = await axios.post(
                        `${API_URL_SERVER}/combined_pnl`,
                        { account_ids: accountId },
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );
                    // For combined PnL, we currently don't support base capital setting 
                    // unless you aggregate them on backend. Setting to 0 for safety.
                    setBaseCapital(0); 
                } else {
                    response = await axios.get(
                        `${API_URL_SERVER}/pnl/${accountId}?strategy=${encodeURIComponent(strategy)}`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );
                    // --- UPDATE: Set base capital from API response ---
                    setBaseCapital(response.data.base_capital || 0);
                }

                const aggregatedPnlData = {};
                response.data.pnl.forEach((row) => {
                    const utcDate = new Date(row.date);
                    const istDate = toDate(utcDate, { timeZone });
                    const year = parseInt(formatInTimeZone(istDate, timeZone, 'yyyy'));
                    const month = parseInt(formatInTimeZone(istDate, timeZone, 'M')) - 1;
                    const day = parseInt(formatInTimeZone(istDate, timeZone, 'd'));

                    if (year === currentYear && month === currentMonth) {
                        aggregatedPnlData[day] = {
                            value: parseFloat(row.daily_pnl),
                            hasData: true,
                        };
                    }
                });

                setPnlData(aggregatedPnlData);
                setBalance(response.data.balance || null);
                setShowBalance(response.data.show_balance || false);
                setMonthlyProfitableTrades(response.data.profitable_trades || 0);
                setMonthlyLosingTrades(response.data.losing_trades || 0);
                setMonthlyTotalLots(response.data.total_lots || 0);
                
                setError('');
            } catch (error) {
                const message = error.response?.data?.message || 'Error fetching PnL data. Please try again later.';
                setError(message);
                console.error('Error fetching PnL:', error.response || error);
            }
        };

        if (accountId && strategy) {
            fetchPnL();
        }
    }, [accountId, strategy, combined, currentMonth, currentYear]);

    const fetchTrades = async (day) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Please log in to view trade history');
                return;
            }

            const date = formatInTimeZone(
                new Date(currentYear, currentMonth, day),
                timeZone,
                'yyyy-MM-dd'
            );

            let aggregatedTrades = [];

            if (combined && Array.isArray(accountId)) {
                const promises = accountId.map(async (id) => {
                    const response = await axios.get(
                        `${API_URL_SERVER}/trades/${id}?date=${date}`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );
                    return (response.data.trades || []).map(trade => ({
                        ...trade,
                        account_id: id 
                    }));
                });

                const results = await Promise.allSettled(promises);
                aggregatedTrades = results.flatMap(({ status, value }) =>
                    status === 'fulfilled' ? value : []
                );
            } else {
                const response = await axios.get(
                    `${API_URL_SERVER}/trades/${accountId}?date=${date}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                aggregatedTrades = response.data.trades || [];
            }

            aggregatedTrades.sort((a, b) => new Date(b.close_time) - new Date(a.close_time));

            setTrades(aggregatedTrades);
            setSelectedDay(day);
            setShowTradesModal(true);
            setError('');
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to load trade data. Please try again later.';
            setError(message);
            console.error('Error fetching trades:', error.response || error);
            setTrades([]);
            setSelectedDay(day);
            setShowTradesModal(true);
        }
    };

    const handleDownloadTrades = async () => {
        if (userRole !== 'admin') {
            setError('Unauthorized: Trade download restricted to admin.');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Please log in to download trades');
                return;
            }

            const startDate = startOfMonth(new Date(currentYear, currentMonth));
            const endDate = endOfMonth(new Date(currentYear, currentMonth));
            const days = eachDayOfInterval({ start: startDate, end: endDate });
            let allTrades = [];

            if (combined && Array.isArray(accountId)) {
                for (const day of days) {
                    const date = formatInTimeZone(day, timeZone, 'yyyy-MM-dd');
                    const promises = accountId.map(async (id) => {
                        const response = await axios.get(
                            `${API_URL_SERVER}/trades/${id}?date=${date}`,
                            {
                                headers: { Authorization: `Bearer ${token}` },
                            }
                        );
                        return (response.data.trades || []).map(trade => ({
                            ...trade,
                            account_id: id 
                        }));
                    });

                    const results = await Promise.allSettled(promises);
                    const dailyTrades = results.flatMap(({ status, value }) =>
                        status === 'fulfilled' ? value : []
                    );
                    allTrades = [...allTrades, ...dailyTrades];
                }
            } else {
                for (const day of days) {
                    const date = formatInTimeZone(day, timeZone, 'yyyy-MM-dd');
                    const response = await axios.get(
                        `${API_URL_SERVER}/trades/${accountId}?date=${date}`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );
                    allTrades = [...allTrades, ...(response.data.trades || [])];
                }
            }

            allTrades.sort((a, b) => new Date(b.close_time) - new Date(a.close_time));

            const headers = [
                'Trade ID',
                combined ? 'Account ID' : 'Account ID',
                'Symbol',
                'Type',
                'Volume',
                'Open Price',
                'Open Time',
                'Close Price',
                'Close Time',
                'Charges',
                'Profit'
            ];

            const csvRows = allTrades.map(trade => [
                trade.trade_id,
                combined ? trade.account_id : accountId,
                trade.symbol,
                trade.type,
                trade.volume,
                trade.open_price.toFixed(2),
                formatInTimeZone(new Date(trade.open_time), timeZone, 'yyyy-MM-dd HH:mm:ss'),
                trade.close_price.toFixed(2),
                formatInTimeZone(new Date(trade.close_time), timeZone, 'yyyy-MM-dd HH:mm:ss'),
                trade.broker_charges.toFixed(2),
                trade.adjusted_profit.toFixed(2)
            ].map(value => `"${value}"`).join(','));

            const csvContent = [headers.join(','), ...csvRows].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `trades_${months[currentMonth]}_${currentYear}_${combined ? 'combined' : accountId}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            setError('');
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to download trades';
            setError(message);
            console.error('Error downloading trades:', error.response || error);
        }
    };

    const handleDayClick = (day) => {
        if (!pnlData[day]?.hasData) return;
        if (!combined && (userRole === 'admin' || (userRole === 'user' && showTrades))) {
            fetchTrades(day);
        }
    };

    const getMonthlyTotalPnL = () => {
        return Object.values(pnlData).reduce((total, day) => total + (day.value || 0), 0);
    };

    const calculateDayTotals = () => {
        const totalPnL = trades.reduce((sum, trade) => sum + parseFloat(trade.adjusted_profit || 0), 0);
        const totalCharges = trades.reduce((sum, trade) => sum + parseFloat(trade.broker_charges || 0), 0);
        const totalTrades = trades.length;
        
        const dailyProfitableTrades = trades.filter(trade => parseFloat(trade.adjusted_profit || 0) > 0).length;
        const dailyLosingTrades = trades.filter(trade => parseFloat(trade.adjusted_profit || 0) < 0).length;
        
        return { totalPnL, totalCharges, totalTrades, dailyProfitableTrades, dailyLosingTrades };
    };

    const { totalPnL, totalCharges, totalTrades, dailyProfitableTrades, dailyLosingTrades } = calculateDayTotals();

    const handleOpenCapitalModal = () => {
        setTempCapitalInput(baseCapital || '');
        setShowCapitalModal(true);
    };

    const handleSaveCapital = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError('Please log in to update capital');
            return;
        }

        const val = parseFloat(tempCapitalInput);
        const finalVal = (!isNaN(val) && val > 0) ? val : 0;

        try {
            await axios.post(`${API_URL_SERVER}/update_base_capital`, {
                account_id: accountId,
                strategy: strategy,
                base_capital: finalVal
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setBaseCapital(finalVal);
            setShowCapitalModal(false);
            setError('');
        } catch (err) {
            console.error("Error updating base capital:", err);
            setError(err.response?.data?.message || "Failed to update capital");
        }
    };

    const getCustomProfitPercentage = () => {
        const monthlyTotal = getMonthlyTotalPnL();
        if (!baseCapital || baseCapital === 0) return null;
        return (monthlyTotal / baseCapital) * 100;
    };

    const customPercentage = getCustomProfitPercentage();

    const formatMoney = (amount) => {
        if (amount === null || amount === undefined) return 'N/A';
        const prefix = amount >= 0 ? '$' : '-$';
        let formattedAmount;
        if (
            formattedAmount = Math.abs(amount).toLocaleString('en-US', { maximumFractionDigits: 2 })
           )
        return `${prefix}${formattedAmount}`;
    };

    const formatPnL = (amount) => {
        if (!amount && amount !== 0) return '';
        return formatMoney(amount);
    };

    const getDayStyle = (day) => {
        if (!day.pnl || !day.pnl.hasData) return '';
        return day.pnl.value >= 0 ? 'positive-pnl' : 'negative-pnl';
    };

    const getDateCellClass = (day) => {
        if (!day.isCurrentMonth) return 'date-cell inactive-month';
        return 'date-cell';
    };

    const getDayNumberClass = (day) => {
        if (!day.pnl || !day.pnl.hasData) return 'day-number';
        return day.pnl.value >= 0 ? 'day-number positive-day' : 'day-number negative-day';
    };

    const changeMonth = (direction) => {
        let newDate = new Date(currentYear, currentMonth);
        newDate = direction > 0 ? addMonths(newDate, 1) : subMonths(newDate, 1);
        setCurrentMonth(newDate.getMonth());
        setCurrentYear(newDate.getFullYear());
    };

    const generateCalendarDays = () => {
        const monthStart = toDate(new Date(currentYear, currentMonth, 1), { timeZone });
        const monthEnd = endOfMonth(monthStart);
        const daysInMonth = parseInt(formatInTimeZone(monthEnd, timeZone, 'd'));
        const firstDayOfMonth = getDay(monthStart);
        const prevMonthEnd = subMonths(monthStart, 1);
        const daysInPrevMonth = parseInt(formatInTimeZone(prevMonthEnd, timeZone, 'd'));
        const prevMonthDays = [];
        for (let i = daysInPrevMonth - firstDayOfMonth + 1; i <= daysInPrevMonth; i++) {
            prevMonthDays.push({ day: i, isCurrentMonth: false, isPrevMonth: true });
        }
        const currentMonthDays = [];
        for (let i = 1; i <= daysInMonth; i++) {
            currentMonthDays.push({ day: i, isCurrentMonth: true, pnl: pnlData[i] });
        }
        const totalDaysDisplayed = prevMonthDays.length + currentMonthDays.length;
        const remainingCells = totalDaysDisplayed % 7 === 0 ? 0 : 7 - (totalDaysDisplayed % 7);
        const nextMonthDays = [];
        for (let i = 1; i <= remainingCells; i++) {
            nextMonthDays.push({ day: i, isCurrentMonth: false, isNextMonth: true });
        }
        return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
    };

    const days = generateCalendarDays();

    const CircleSVG = ({ color, size = '1em' }) => (
        <svg 
            xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 20 20" fill={color}
            style={{ marginRight: '5px', verticalAlign: 'middle' }}
        >
            <circle cx="8" cy="8" r="8"/>
        </svg>
    );

    return (
        <div className="pnl-calendar-container">
            {error && <div className="alert alert-danger">{error}</div>}
            
            <div className="month-navigation-container">
                <button className="month-nav-btn" onClick={() => changeMonth(-1)}>
                    {months[(currentMonth - 1 + 12) % 12]}
                </button>
                <h2 className="calendar-title">
                    {combined ? 'Combined ' : ''}PnL for {months[currentMonth]} {currentYear}
                </h2>
                <button className="month-nav-btn" onClick={() => changeMonth(1)}>
                    {months[(currentMonth + 1) % 12]}
                </button>
            </div>
            
            <div className='d-flex justify-content-between align-items-center'>
                
                {/* Left side: Buttons */}
                <div className='d-flex justify-content-start align-items-center mb-3'>
                    {userRole === 'admin' && (
                        <button
                            className="btn btn-success"
                            onClick={handleDownloadTrades}
                            title="Download Trades CSV"
                            style={{ padding: '1px 7px 5px 7px' }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                            </svg>
                        </button>
                    )}
                    
                    {/* Disable Set Capital button if combined view */}
                    
                    {userRole === 'admin' && !combined && (
                        <button 
                            className="btn btn-success ms-2"
                            onClick={handleOpenCapitalModal}
                            title="Set Base Capital for % calculation"
                            style={{padding: '1px 7px 5px 7px'}}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-calculator" viewBox="0 0 16 16">
                                <path d="M12 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"/>
                                <path d="M4 2.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5zm0 4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5z"/>
                            </svg>
                        </button>
                    )}

                        {/* Display Percentage */}
                        {userRole === 'admin' && customPercentage !== null && (
                            <span 
                                className="ms-2" 
                                style={{ 
                                    fontSize: '0.9em', 
                                    color: customPercentage >= 0 ? '#28a745' : '#dc3545',
                                    fontWeight: 'bold'
                                }}
                            >
                                ({customPercentage > 0 ? '+' : ''}{customPercentage.toFixed(2)}%)
                            </span>
                        )}
                </div>
                
                {/* Right side: All Metrics */}
                <div className='d-flex flex-column align-items-end'>
                    <div className="total-pnl">
                        Total PnL:{' '}
                        <span className={getMonthlyTotalPnL() >= 0 ? 'positive-amount' : 'negative-amount'}>
                            ${getMonthlyTotalPnL().toLocaleString('en-US', { maximumFractionDigits: 2 })}
                        </span>
                    </div>
                    
                    {showBalance && balance !== null && (
                        <div className="account-balance total-pnl mb-3">
                            Account Balance:{' '}
                            <span className={balance >= 0 ? 'positive-amount' : 'negative-amount'}>
                                {formatMoney(balance)}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            
            <div className="calendar-grid">
                <div className="weekday-header">Sun</div>
                <div className="weekday-header">Mon</div>
                <div className="weekday-header">Tue</div>
                <div className="weekday-header">Wed</div>
                <div className="weekday-header">Thu</div>
                <div className="weekday-header">Fri</div>
                <div className="weekday-header">Sat</div>
                {days.map((day, index) => (
                    <div
                        key={index}
                        className={getDateCellClass(day)}
                        onClick={() => day.isCurrentMonth && handleDayClick(day.day)}
                        style={{ cursor: day.isCurrentMonth && day.pnl?.hasData && !combined ? 'pointer' : 'default' }}
                    >
                        <div className={getDayNumberClass(day)}>{day.day}</div>
                        {day.pnl && day.pnl.hasData && (
                            <div className={`pnl-value ${getDayStyle(day)}`}>
                                {formatPnL(day.pnl.value)}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {showCapitalModal && (
                <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-sm">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Set Base Capital</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCapitalModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="capitalInput" className="form-label">Starting Amount ($)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="capitalInput"
                                        placeholder="e.g. 1000"
                                        value={tempCapitalInput}
                                        onChange={(e) => setTempCapitalInput(e.target.value)}
                                    />
                                    <div className="form-text">
                                        Enter your starting balance for this month to calculate profit %.
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCapitalModal(false)}>Close</button>
                                <button type="button" className="btn btn-primary" onClick={handleSaveCapital}>Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {showTradesModal && (
                <div
                    className="modal"
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                >
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <div className="modal-title">
                                    <div>
                                        <h5>
                                            {months[currentMonth]} {selectedDay}, {currentYear}
                                        </h5>
                                    </div>
                                </div>
                                <div className='flex_display d-flex' style={{ textAlign: 'right', float: 'right', marginLeft: 'auto' }}>
                                    <span className='me-md-3 me-sm-0'>
                                        <strong>Trades: </strong>{totalTrades}
                                    </span>
                                    <div>
                                    <span className='me-2' style={{ color: 'green' }}>
                                        <CircleSVG color="#28a745" />
                                        <span className='positive-amount'>{dailyProfitableTrades}</span>
                                    </span>
                                    <span className='me-2' style={{ color: 'red' }}>
                                        <CircleSVG color="#dc3545" />
                                        <span className='negative-amount'>{dailyLosingTrades}</span>
                                    </span>
                                    </div>
                                    <span className='me-md-3 me-sm-0'>
                                        <strong>Charges: </strong>${totalCharges.toFixed(2)}
                                    </span>
                                    <span style={{ color: totalPnL >= 0 ? 'green' : 'red' }}>
                                        <strong>PnL: </strong>${totalPnL.toFixed(2)}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowTradesModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {trades.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-striped">
                                            <thead>
                                                <tr>
                                                    <th>Trade ID</th>
                                                    {combined && <th>Account ID</th>}
                                                    <th>Symbol</th>
                                                    <th>Type</th>
                                                    <th>Volume</th>
                                                    <th>Open Price</th>
                                                    <th>Close Price</th>
                                                    <th>Charges</th>
                                                    <th>Profit</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {trades.map((trade) => (
                                                    <tr key={trade.trade_id}>
                                                        <td>{trade.trade_id}</td>
                                                        {combined && <td>{trade.account_id}</td>}
                                                        <td>{trade.symbol}</td>
                                                        <td>{trade.type}</td>
                                                        <td>{trade.volume}</td>
                                                        <td>
                                                            {trade.open_price.toFixed(2)}
                                                            <p className='time'>
                                                                (
                                                                {formatInTimeZone(
                                                                    new Date(trade.open_time),
                                                                    timeZone,
                                                                    'HH:mm:ss'
                                                                )}
                                                                )
                                                            </p>
                                                        </td>
                                                        <td>
                                                            {trade.close_price.toFixed(2)}
                                                            <p className='time'>
                                                                (
                                                                {formatInTimeZone(
                                                                    new Date(trade.close_time),
                                                                    timeZone,
                                                                    'HH:mm:ss'
                                                                )}
                                                                )
                                                            </p>
                                                        </td>
                                                        <td>${trade.broker_charges.toFixed(2)}</td>
                                                        <td
                                                            style={{
                                                                color: trade.adjusted_profit >= 0 ? 'green' : 'red',
                                                            }}
                                                        >
                                                            ${trade.adjusted_profit.toFixed(2)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p>No trades found for this day.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {userRole === 'admin' && (
                <div className="bottom_pnl monthly-stats total-pnl text-center mt-2 ">
                    {/* NEW: Total Lots Display */}
                    <span className='px-2'>
                        Lots: {monthlyTotalLots.toFixed(2)}
                    </span>
                    
                    <span className='px-2'>
                        <CircleSVG color="#28a745" />
                        <span className='positive-amount'>{monthlyProfitableTrades}</span>
                    </span>
                    <span className='px-2'>
                        <CircleSVG color="#dc3545" />
                        <span className='negative-amount'>{monthlyLosingTrades}</span>
                    </span>
                </div>
            )}
        </div>
    );
};

export default PnLCalendar;